/**
 * EMPIRIC CRO v1 (product page + cart), 2026-09-18. Round 2 revisions 2026-09-19.
 * v2 (2026-09-19): Fit Finder unit selectors, static "Same principle" support.
 * Loaded (deferred) by sections/main-product.liquid, sections/main-cart-footer.liquid
 * and sections/cro-size-guide-page.liquid. Guarded so it only initialises once per page.
 *
 * Modules:
 *  1. Buy-button label sync ("GET YOURS" is the label used in the theme; Dawn's
 *     product-form.js re-applies window.variantStrings.addToCart after a sold-out
 *     variant change, so that string is kept in sync here).
 *  2. Default colour: with no ?variant= in the URL, pre-select the "Black" colour
 *     value (case-insensitive) through the theme's own change event so price,
 *     media and URL update exactly as a user click would.
 *  3. Sticky bottom bar (mobile): shown once the main GET YOURS button has scrolled
 *     out of view; submits the same product form. 3b: the cart page equivalent,
 *     a sticky Check out bar hidden while the real button is on screen.
 *  4. Delivery estimate (2026-10-05): one estimated arrival date instead of the
 *     old business-day ranges (standard 9-14, priority 7-9). Today plus
 *     data-eta-days calendar days (theme setting "Delivery estimate", default 8),
 *     moved past Sundays and US postal holidays, rendered as "Tue, Oct 13".
 *     Modes: line (PDP), accordion (PDP), cart (cart page note, re-applied after
 *     cart updates) and notification (add-to-cart popup).
 *  5. Size guide unit toggle + Fit Finder. v2: a unit selector next to each
 *     field (height cm or ft / in, weight kg or lb) with numeric-only inputs
 *     (snippets/cro-fit-inputs.liquid). Values convert internally, the
 *     interpreted height and weight show live under the fields before the
 *     recommendation, and typed formats (1.80, 5.11, 5'11, 180cm, 187 lb) are
 *     still understood. Never recommends sizing down ("Oversized, as intended"
 *     = usual size, "Extra oversized" = one size up, capped at the largest size).
 *     Crops (2026-10-05): "Close crop, as intended" = the crop table size, where
 *     between sizes goes up, and "More room, slightly longer" = one size up,
 *     capped at L. The size guide page lists both crop sizes.
 *     Essential Heavyweight Hoodie (2026-10-05, category 'hoodie-essential'):
 *     "EMPIRIC recommended fit (relaxed)" = one size up from the usual size,
 *     the default, and "Closer, more fitted look" = the usual size. The Snow
 *     Washed hoodie stays on 'hoodie'.
 *     Adds the "Fit Finder" line-item property to the add-to-cart form. A form
 *     with data-category="all" (size guide page) reports every category at once.
 *  6. "Same principle, other builds": rendered server-side from the design tag
 *     when snippets/cro-same-principle.liquid finds siblings (data-static);
 *     otherwise a predictive-search lookup by normalized design name (plus a
 *     first-two-words fallback), containment match, hats excluded, ordered
 *     tee > crop > hoodie(s) > tank, hidden when nothing matches.
 *  7. "Complete the set" (Sets launch, 2026-09-25): matching sweatpants on a
 *     Snow Washed hoodie PDP and the matching hoodie(s) on the sweatpants PDP
 *     (snippets/cro-complete-the-set.liquid). Partner colour and size follow
 *     the main product's selection, pills override, "Add both to cart" posts
 *     both lines to /cart/add.js and opens the cart notification with both.
 *     Fit Finder: 'pants' category (usual size, never sizes down, S to 2XL).
 *     Snow Washed tank (2026-10-08): 'tank-snow' category (loose fit, usual size,
 *     never sizes down, M to 2XL). When the table size is below the smallest size
 *     a style offers (S on an M to 2XL style), the result says so.
 *     Priority timing (2026-10-08): the Delivery accordion adds "(about N days)"
 *     after the Priority price when the element carries data-priority-days.
 *     Sizing update (2026-10-15): the Women's Crop Tee ('crop'), the Essential
 *     Heavyweight Hoodie ('hoodie-essential') and the Snow Washed Hoodie
 *     ('hoodie') size from the customer's usual size (USUAL_SIZE_FITS below):
 *     the optional "Usual size" field, or else the height/weight table size.
 *     Crop: one size up (recommended) or the usual size (fitted look). Essential:
 *     one size up (recommended) or two sizes up (extra oversized). Snow Washed:
 *     the usual size (recommended) or one size up (extra oversized). Only sizes
 *     the product is made in are recommended, never the largest size as a
 *     fallback. The crop table rounds at the midpoints again, so the size up is
 *     not applied twice. Tees, tanks and sweatpants are unchanged.
 *     Sizing update, second pass (2026-10-15): one sizing path for every
 *     category (module 5, FITS). Metric and imperial entries are rounded to
 *     the same whole inches and kilograms first (canonicalBody), so an entry
 *     and its equivalent in the other system always get the same result
 *     (178 cm / 73 kg and 5'10" / 161 lb were L and M before). The usual size
 *     comes from the "Usual size" field, now on every Fit Finder, or from the
 *     height/weight table: unisex (XS to 4XL) or women's (crop tee, ribbed
 *     racerback tank), where height adds one size at most. No size is
 *     recommended when a style is not made in the size needed (above the
 *     range, or two sizes below it), and sold-out sizes are flagged. Notes say
 *     when the usual size is an estimate, when someone sits close to the line
 *     between two sizes, and when a size fits closer than the style's look.
 *     data-cro-ff-jump opens the size guide straight at the Fit Finder.
 *     Final decisions (Roy, 2026-10-15): the women's ribbed racerback is true
 *     to size for its close, fitted look, with "Relaxed fit: one size up" as
 *     the second choice. The Essential hoodie stays one size up (two for extra
 *     oversized) and the Snow Washed hoodie stays true to size (one up for
 *     extra oversized). Tees stop at 2XL: when the intended oversized fit would
 *     need more than 2XL (a usual 2XL or bigger), the finder says the size is
 *     not available instead of recommending 2XL. Messages describe how the
 *     garment fits, never the body.
 *     The earlier notes in this block describe the rules each update replaced.
     One recommendation (Roy, 2026-10-19): customers on or close to the line
     between two sizes (one inch or two kilograms from the next size) get the
     larger one, when the style is made in it. Customers further inside a size
     keep that size. The "Usual size" field, the fit choices (Extra oversized,
     Fitted look, Relaxed fit) and every "your usual size" wording are gone, so
     each style shows one recommended size. Garment measurements, size charts
     and the Fit Promise are unchanged.
     Essential repositioning (Roy, 2026-10-21): the Essential hoodie result
     reads "for a regular, comfortable fit" (was "for the intended relaxed
     fit"). It is a regular fit, not oversized, and still runs small, so the
     recommendation stays one size up from the table size.
     Women's sweatshirt (Roy, 2026-10-21): category 'sweatshirt', women's
     table, S to L, the table size as is (usual size), no between-sizes
     step, honest notes above the range. Its color row opens on the first
     color (data-cro-keep-first) instead of Black.
 */
(function () {
  if (window.__croV1) return;
  window.__croV1 = true;

  var CTA_LABEL = 'GET YOURS';
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function onVariantChange(handler) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handler);
    }
  }

  function normalize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  /* ---------------------------------------------------------------- 1. label */
  function syncLabelStrings() {
    if (window.variantStrings) window.variantStrings.addToCart = CTA_LABEL;
  }

  /* --------------------------------------------------------- 2. default colour */
  function selectDefaultColour() {
    if (/[?&]variant=/.test(window.location.search)) return;
    document.querySelectorAll('product-info variant-selects').forEach(function (selects) {
      // Products that should open on their first color (the women's sweatshirt) skip this.
      if (selects.hasAttribute('data-cro-keep-first')) return;
      var fieldsets = selects.querySelectorAll('.product-form__input');
      fieldsets.forEach(function (fieldset) {
        var legend = fieldset.querySelector('legend, label.form__label');
        var name = legend ? legend.textContent.trim().toLowerCase() : '';
        if (!/^colou?r/.test(name)) return;

        var radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        if (radios.length > 1) {
          // Exact "Black" first, then a value containing the word (e.g. "Natural / Black").
          var black = radios.find(function (r) {
            return String(r.value).trim().toLowerCase() === 'black' && !r.classList.contains('disabled');
          }) || radios.find(function (r) {
            return /\bblack\b/i.test(String(r.value)) && !r.classList.contains('disabled');
          });
          if (black && !black.checked) {
            black.checked = true;
            black.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        var select = fieldset.querySelector('select');
        if (select && select.options.length > 1) {
          var option = Array.from(select.options).find(function (o) {
            return String(o.value).trim().toLowerCase() === 'black';
          }) || Array.from(select.options).find(function (o) {
            return /\bblack\b/i.test(String(o.value));
          });
          if (option && !option.selected) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------- 3. sticky bar */
  function initStickyBar() {
    document.querySelectorAll('.cro-sticky').forEach(function (bar) {
      var sectionId = bar.getAttribute('data-section');
      var mainButton = document.getElementById('ProductSubmitButton-' + sectionId);
      var stickyButton = bar.querySelector('.cro-sticky__btn');
      var priceEl = bar.querySelector('.cro-sticky__price');
      if (!mainButton || !stickyButton) return;

      // Fixed positioning must not sit inside a transformed ancestor (Dawn's
      // scroll-reveal animation transforms the info column), so move to <body>.
      document.body.appendChild(bar);

      function syncState() {
        var span = mainButton.querySelector('span');
        var text = span ? span.textContent.trim() : CTA_LABEL;
        stickyButton.textContent = text || CTA_LABEL;
        stickyButton.disabled = mainButton.hasAttribute('disabled') || mainButton.getAttribute('aria-disabled') === 'true';
        var price = document.querySelector('#price-' + sectionId + ' .price');
        if (price && priceEl) {
          var item = price.classList.contains('price--on-sale')
            ? price.querySelector('.price-item--sale')
            : price.querySelector('.price__regular .price-item--regular');
          if (item) priceEl.textContent = item.textContent.trim();
        }
      }

      function setVisible(visible) {
        bar.classList.toggle('is-visible', visible);
        document.body.classList.toggle('cro-sticky-active', visible);
      }

      // Show once the main button has scrolled out of view above the viewport.
      var ticking = false;
      function update() {
        ticking = false;
        var rect = mainButton.getBoundingClientRect();
        setVisible(rect.bottom < 0);
      }
      function onScroll() {
        if (ticking) return;
        ticking = true;
        // rAF is paused in background tabs; fall back to a timeout there.
        if (document.hidden) window.setTimeout(update, 0);
        else window.requestAnimationFrame(update);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(onScroll, { threshold: 0 }).observe(mainButton);
      }
      update();

      syncState();
      onVariantChange(function () {
        window.setTimeout(syncState, 0);
      });
      mainButton.addEventListener('click', syncState);
    });
  }

  /* ---------------------------------------------------- 3b. cart sticky checkout */
  function initCartSticky() {
    var bar = document.querySelector('[data-cro-cart-sticky]');
    if (!bar) return;
    var totalEl = bar.querySelector('[data-cro-cart-total]');
    var button = bar.querySelector('.cro-cart-sticky__btn');

    function checkoutButton() {
      return document.getElementById('checkout');
    }

    function setHidden(hidden) {
      bar.classList.toggle('is-hidden', hidden);
      bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      document.body.classList.toggle('cro-cart-sticky-active', !hidden);
    }

    var ticking = false;
    function update() {
      ticking = false;
      var checkout = checkoutButton();
      var empty = !checkout || checkout.hasAttribute('disabled');
      if (button) button.disabled = empty;
      var total = document.querySelector('#main-cart-footer .totals__total-value');
      if (total && totalEl) {
        var text = total.textContent.trim();
        // Only write on change: the footer is observed for childList mutations.
        if (totalEl.textContent.trim() !== text) totalEl.textContent = text;
      }
      if (empty) return setHidden(true);
      var rect = checkout.getBoundingClientRect();
      var onScreen = rect.bottom > 0 && rect.top < window.innerHeight;
      setHidden(onScreen);
    }
    function schedule() {
      if (ticking) return;
      ticking = true;
      if (document.hidden) window.setTimeout(update, 0);
      else window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    if ('MutationObserver' in window) {
      var root = document.getElementById('main-cart-footer') || document.body;
      new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
      var items = document.getElementById('main-cart-items');
      if (items) new MutationObserver(schedule).observe(items, { childList: true, subtree: true });
    }
    update();
  }

  /* ------------------------------------------------------ 4. delivery estimate */
  // One estimated arrival date instead of a business-day range (2026-10-05).
  // Basis: recent US orders arrived a median of 7 calendar days after the order,
  // about 4 in 5 within 8 days. The theme setting "Delivery estimate" feeds
  // data-eta-days and data-typical-days, so the estimate can be tuned without
  // code. The date never lands on a Sunday or a US postal holiday, it moves to
  // the next delivery day instead.
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var NO_DELIVERY = [
    '2026-10-12', '2026-11-11', '2026-11-26', '2026-12-25',
    '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31', '2027-06-18', '2027-06-19',
    '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25',
    '2027-12-24', '2027-12-25', '2027-12-31', '2028-01-01',
  ];

  function isoDay(d) {
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  function estimateArrival(from, days) {
    var d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    d.setDate(d.getDate() + days);
    for (var i = 0; i < 7 && (d.getDay() === 0 || NO_DELIVERY.indexOf(isoDay(d)) !== -1); i++) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  // "Tue, Oct 13"
  function formatDay(d) {
    return DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate();
  }

  function readDays(el, name, fallback) {
    var n = parseInt(el.getAttribute(name), 10);
    return isNaN(n) || n < 1 || n > 60 ? fallback : n;
  }

  function renderDelivery() {
    var today = new Date();
    document.querySelectorAll('[data-cro-delivery]').forEach(function (el) {
      var etaDays = readDays(el, 'data-eta-days', 8);
      var typicalDays = readDays(el, 'data-typical-days', 7);
      var date = formatDay(estimateArrival(today, etaDays));
      if (el.getAttribute('data-cro-eta') === date) return;
      var standardPrice = el.getAttribute('data-standard-price') || '5.99';
      var priorityPrice = el.getAttribute('data-priority-price') || '12.99';
      var freeOver = el.getAttribute('data-free-over') || '100';
      var mode = el.getAttribute('data-cro-delivery');
      var html;
      if (mode === 'accordion') {
        html =
          '<p>Made to order for you, in the size and color you choose.</p>' +
          '<p><strong>Estimated arrival: ' + date + '.</strong> Based on our recent orders, most arrive in about ' + typicalDays + ' days.</p>' +
          '<p><strong>Standard $' + standardPrice + '</strong> (free over $' + freeOver + '). <strong>Priority $' + priorityPrice + '</strong>' + (el.getAttribute('data-priority-days') ? ' (about ' + el.getAttribute('data-priority-days') + ' days)' : '') + ' is also available at checkout.</p>';
      } else if (mode === 'cart') {
        html = 'Made to order. Estimated arrival: <strong>' + date + '</strong>. Fit Promise applies.';
      } else if (mode === 'notification') {
        html = 'Estimated arrival: <strong>' + date + '</strong>. Most orders arrive in about ' + typicalDays + ' days.';
      } else {
        html =
          '<span class="cro-delivery__eta">Estimated arrival: <strong>' + date + '</strong></span> ' +
          'Made to order for you. Most orders arrive in about ' + typicalDays + ' days. Standard <strong>$' + standardPrice + '</strong>, free over $' + freeOver + '.';
      }
      el.innerHTML = html;
      el.setAttribute('data-cro-eta', date);
    });
  }

  // The cart page footer (.js-contents) is re-rendered after every quantity change,
  // which brings back the server fallback line. Re-apply the estimate when it does.
  function watchCartFooter() {
    var footer = document.getElementById('main-cart-footer');
    if (!footer || typeof MutationObserver === 'undefined') return;
    new MutationObserver(function () {
      renderDelivery();
    }).observe(footer, { childList: true, subtree: true });
  }

  /* --------------------------------------------- 5. size guide + fit finder */
  // Sizing logic (2026-10-19). One path for every category and for both unit
  // systems, and one recommendation per style:
  //  1. Canonical measurements (canonicalBody): height in whole inches and
  //     weight in whole kilograms, each rounded the way the same measurement
  //     would be typed in the other system. Equivalent entries (178 cm and
  //     5'10", 73 kg and 161 lb, 179 cm and 5'10.5") land on the same numbers,
  //     so they always get the same size.
  //  2. The table size: an estimate from height and weight with the table for
  //     the style (SIZE_TABLES: unisex, or women's for the crop tee and the
  //     ribbed racerback tank). Height can raise it by one size at most,
  //     because a size up adds far more room around the body than length.
  //     There is no "usual size" field any more (Roy, 2026-10-19).
  //  3. Between two sizes (NEAR_UP_STEPS): when one inch more height or two
  //     kilograms (about 4 lb) more weight would already give the next size,
  //     the customer is on or close to the line between two sizes and gets the
  //     larger one, when the style is made in it. Everyone further inside a
  //     size keeps that size.
  //  4. The fit (FITS): one fit per style, sizes up from the table size where
  //     the style runs small (crop tee, Essential hoodie). Only sizes the
  //     product is made in are recommended. Above the range there is no
  //     recommendation, never the largest size as a fallback. Below it the
  //     smallest size is offered only when it is one size off, with a note that
  //     it fits roomier.
  var SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

  var DEFAULT_SIZES = {
    tee: ['S', 'M', 'L', 'XL', '2XL'],
    hoodie: ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    'hoodie-essential': ['S', 'M', 'L', 'XL', '2XL', '3XL'],
    crop: ['S', 'M', 'L'],
    sweatshirt: ['S', 'M', 'L'],
    tank: ['S', 'M', 'L', 'XL'],
    'tank-snow': ['M', 'L', 'XL', '2XL'],
    pants: ['S', 'M', 'L', 'XL', '2XL'],
  };

  var CM_PER_IN = 2.54;
  var LB_PER_KG = 2.20462;

  // Canonical body measurements: whole inches and whole kilograms. Each entry
  // is rounded the way the same measurement would be typed in the other
  // system, so an entry and its equivalent always give the same numbers:
  //  - height typed in cm or m: the nearest whole inch (178 cm -> 70 in,
  //    which is 5'10");
  //  - height typed in feet and inches: through the nearest whole cm first
  //    (5'10" -> 178 cm -> 70 in, 5'10.5" -> 179 cm -> 70 in);
  //  - weight typed in lb: the nearest whole kg (161 lb -> 73 kg);
  //  - weight typed in kg: through the nearest whole lb first (73 kg -> 161 lb
  //    -> 73 kg, 72.5 kg -> 160 lb -> 73 kg).
  // metric: how the value was typed. When it is not known, a whole or half
  // inch counts as typed in inches and a weight in whole or tenth pounds as
  // typed in pounds.
  function canonicalHeightIn(heightIn, metric) {
    if (metric === undefined) metric = !isHalfStep(heightIn, 2);
    return metric ? Math.round(heightIn) : Math.round(Math.round(heightIn * CM_PER_IN) / CM_PER_IN);
  }

  function canonicalWeightKg(weightLb, metric) {
    if (metric === undefined) metric = !isHalfStep(weightLb, 10);
    return metric ? Math.round(Math.round(weightLb) / LB_PER_KG) : Math.round(weightLb / LB_PER_KG);
  }

  // height: { inches, metric }, weight: { lb, metric } (the field readers), or
  // plain numbers in inches and pounds.
  function canonicalBody(height, weight) {
    var h = typeof height === 'number' ? { inches: height } : height;
    var w = typeof weight === 'number' ? { lb: weight } : weight;
    return { heightIn: canonicalHeightIn(h.inches, h.metric), weightKg: canonicalWeightKg(w.lb, w.metric) };
  }

  // Usual-size tables. Upper limits, inclusive, in canonical units (whole
  // inches, whole kilograms). A value above the last limit takes the next size.
  var SIZE_TABLES = {
    // Unisex and men's sizes. S to 2XL keep the original EMPIRIC ranges
    // (S up to 5'8"/150 lb, M 5'7"-5'11"/145-175 lb, L 5'10"-6'2"/170-200 lb,
    // XL 6'0"-6'4"/195-230 lb, 2XL over 6'2" or 225 lb) split in the middle of
    // each overlap: 5'7"|5'8", 5'10"|5'11", 6'1"|6'2", 6'3"|6'4" and 147.5,
    // 172.5, 197.5, 227.5 lb (66.9, 78.2, 89.6, 103.2 kg). XS (up to 5'4" or
    // 121 lb), 3XL (over 256 lb) and 4XL (over 287 lb) extend the table, so
    // the finder knows when a style is not made in someone's size instead of
    // stopping at 2XL. Height alone never goes past 2XL.
    unisex: {
      key: 'unisex',
      sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'],
      heightIn: [64, 67, 70, 73, 75],
      weightKg: [55, 66, 78, 89, 103, 116, 130],
    },
    // Women's sizes: the women's crop tee and the ribbed racerback tank. S to
    // L keep the original crop ranges (S up to 5'5"/130 lb, M 5'4"-5'8"/
    // 125-155 lb, L from 5'7" or 150 lb) split in the middle: 5'4"|5'5",
    // 5'7"|5'8", 127.5 lb (57.8 kg) and 152.5 lb (69.2 kg). XS (up to 5'1" or
    // 101 lb), XL (over 176 lb) and 2XL (over 201 lb) extend the table.
    womens: {
      key: 'womens',
      sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL'],
      heightIn: [61, 64, 67, 70],
      weightKg: [46, 57, 69, 80, 91],
    },
  };

  function tableFor(category) {
    return category === 'crop' || category === 'tank' || category === 'sweatshirt' ? SIZE_TABLES.womens : SIZE_TABLES.unisex;
  }

  function indexFor(value, bounds) {
    for (var i = 0; i < bounds.length; i += 1) {
      if (value <= bounds[i]) return i;
    }
    return bounds.length;
  }

  /**
   * Usual size from canonical height and weight (canonicalBody). Weight sets
   * the size, and height can add one size when it points higher (a size up
   * adds about 2 cm of length but 4 to 6 cm around the body). tall / short:
   * height and weight point two or more sizes apart.
   */
  function usualIndex(table, h, kg) {
    var byWeight = indexFor(kg, table.weightKg);
    return Math.max(byWeight, Math.min(indexFor(h, table.heightIn), byWeight + 1));
  }

  // Between two sizes (Roy, 2026-10-19): one inch more height or two kilograms
  // (about 4 lb) more weight would already give the next size. That is about
  // the part of each size where EMPIRIC's original chart overlapped the next
  // size (L 170 to 200 lb, XL 195 to 230 lb). In a simulated audience it
  // moves about 1 in 6 hoodie and tee shoppers up one size.
  var NEAR_UP_STEPS = [[1, 0], [0, 2]];

  function estimateUsual(category, body) {
    var table = tableFor(category);
    var h = body.heightIn;
    var kg = body.weightKg;
    var byHeight = indexFor(h, table.heightIn);
    var byWeight = indexFor(kg, table.weightKg);
    var index = usualIndex(table, h, kg);
    // nearUp: the next size when the customer is on or close to the line
    // between this size and the next one, else ''. A step this small never
    // moves the estimate by more than one size.
    var nearUp = '';
    if (index + 1 < table.sizes.length) {
      NEAR_UP_STEPS.forEach(function (step) {
        if (usualIndex(table, h + step[0], kg + step[1]) > index) nearUp = table.sizes[index + 1];
      });
    }
    return {
      table: table.key,
      size: table.sizes[index],
      heightIn: h,
      weightKg: kg,
      nearUp: nearUp,
      tall: byHeight - byWeight >= 2,
      short: byWeight - byHeight >= 2,
    };
  }

  /* ---- fits: one fit per style, sizes up from the table size ---- */
  // offset = sizes up from the table size (the crop tee and the Essential
  // hoodie run small). why ends the result line ("Recommended: XL, for the
  // intended relaxed, oversized fit"). Since 2026-10-19 every style has one
  // fit and one recommendation: no "Extra oversized", "Fitted look" or
  // "Relaxed fit" choices, and no usual size in the wording (Roy).
  var FITS = {
    // maxUsual: the oversized look holds up to a table size of XL. On a table
    // 2XL the 2XL tee (54.3 in around the chest) has about 4 in of room, like a
    // regular tee, and the tee is not made in 3XL, so no tee size gives the
    // intended oversized fit (Roy, 2026-10-15: 2XL is the maximum, never
    // recommend 2XL when it would not give the intended oversized fit).
    tee: {
      offset: 0, name: 'oversized fit', why: 'oversized as intended',
      maxUsual: 'XL', shortOfLook: 'would fit you like a regular tee rather than oversized',
    },
    hoodie: { offset: 0, name: 'relaxed, oversized fit', why: 'for the intended relaxed, oversized fit' },
    'hoodie-essential': { offset: 1, name: 'regular fit', why: 'for a regular, comfortable fit' },
    crop: { offset: 1, name: 'fitted crop', why: 'for a comfortable fit in this fitted crop' },
    // Women's ribbed racerback (Roy, 2026-10-15): true to size for the intended
    // close, fitted look.
    tank: { offset: 0, name: 'close, fitted look', why: 'for the intended close, fitted look' },
    'tank-snow': { offset: 0, name: 'loose fit', why: 'loose fit as intended' },
    // Women's embroidered sweatshirt (Roy, 2026-10-21): oversized by design,
    // the usual size gives the intended fit and it is never sized up, not
    // even between two sizes (noSizeUp). The L is cut very roomy (27.6 in
    // pit to pit), so one size above the range fits relaxed, not tight
    // (aboveNotes, by how many sizes the need is above the largest size).
    sweatshirt: {
      offset: 0, name: 'oversized fit', why: 'for the intended oversized fit', noSizeUp: true,
      aboveNotes: ['', 'is cut roomy, so it would likely fit you relaxed rather than oversized', 'would likely fit closer than intended'],
    },
    pants: { offset: 0, name: 'relaxed fit', why: 'relaxed fit as intended' },
  };

  // Styles that run small or are cut close: their largest size is too small
  // one size sooner.
  var RUNS_SMALL = { crop: true, 'hoodie-essential': true, tank: true };

  // Where the recommended size fits closer than the style's intended look,
  // by table size, from the garment charts (snippets/cro-size-rows.liquid).
  // Chest room over a typical body for that size: Snow Washed hoodie 2XL about
  // 3 in and 3XL about 1.5 in (M about 9 in), Snow Washed tank 2XL about 2 in
  // (M about 6 in). Sweatpants 2XL hip about 0 in (M about 4 in). The tee at
  // 2XL (about 4 in, M about 7 in) is handled by FITS.tee maxUsual instead.
  // Not shown when the customer was sized up for being between two sizes.
  var CLOSE_FIT = {
    hoodie: {
      '2XL': 'At your size this hoodie fits closer than our relaxed, oversized look.',
      '3XL': '3XL is our largest size. At your size it fits closer than our relaxed, oversized look.',
    },
    'tank-snow': { '2XL': '2XL is the largest size in this style. At your size it fits closer than the intended loose look.' },
    pants: { '2XL': '2XL is the largest size in this style. At your size it fits closer than the intended relaxed look.' },
  };

  var CONTACT = 'Questions about fit? Email hello@empiricwear.com before you order.';

  var BETWEEN_NOTE = "You're close to the line between two sizes, so we recommend the larger one for a comfortable fit.";

  // Size guide page rows, in page order.
  var ALL_CATEGORIES = [
    { key: 'tee', label: 'Tees' },
    { key: 'hoodie-essential', label: 'Essential hoodie' },
    { key: 'hoodie', label: 'Snow Washed hoodie' },
    { key: 'crop', label: "Women's crop tees" },
    { key: 'tank', label: "Women's racerback tank" },
    { key: 'sweatshirt', label: "Women's sweatshirt" },
    { key: 'tank-snow', label: 'Snow Washed tank' },
    { key: 'pants', label: 'Sweatpants' },
  ];

  // Accepted ranges (inches / pounds): 4'0"-7'6" (122-229 cm), 60-450 lb (28-204 kg).
  var HEIGHT_MIN_IN = 48;
  var HEIGHT_MAX_IN = 90;
  var WEIGHT_MIN_LB = 60;
  var WEIGHT_MAX_LB = 450;

  // "XXL" and "2XL" are the same size.
  function canonicalSize(size) {
    var s = String(size || '').trim().toUpperCase();
    if (s === 'XXL') return '2XL';
    if (s === 'XXXL') return '3XL';
    if (s === 'XXXXL') return '4XL';
    return s;
  }

  function canonicalRank(size) {
    return SIZE_ORDER.indexOf(canonicalSize(size));
  }

  // Exact position in SIZE_ORDER (used by "Complete the set", module 7).
  function sizeRank(size) {
    return SIZE_ORDER.indexOf(String(size || '').trim().toUpperCase());
  }

  function sizesText(steps) {
    return steps === 1 ? 'one size' : steps === 2 ? 'two sizes' : steps + ' sizes';
  }

  /**
   * The recommendation for one style.
   * body: canonical height and weight, { heightIn, weightKg } (canonicalBody).
   * available: the product's Size option values (falls back to the category
   * defaults).
   * Returns { category, usual, usualRank, estimate, sizes, range, smallest,
   * largest, fit, base, chosen, sizedUp }. usual is the table size from height
   * and weight (internal, never shown). base is the size for the table size,
   * chosen the recommendation: the next size up when the customer is between
   * two sizes (estimate.nearUp) and the style is made in it, else base.
   * Each is { size, needed, status, made }. status: 'exact' (made),
   * 'smallest' (one size below the range, so the smallest size, made),
   * 'above' or 'below' (not made, size null), 'missing' (a size inside the
   * range that this product skips) or 'look' (the size exists but would not
   * give the intended look, see FITS.tee maxUsual).
   */
  function recommendFor(category, body, available) {
    var fit = FITS[category] || FITS.tee;
    var sizes = (available && available.length ? available : DEFAULT_SIZES[category] || DEFAULT_SIZES.tee).filter(function (s) {
      return canonicalRank(s) > -1;
    });
    if (!sizes.length) sizes = (DEFAULT_SIZES[category] || DEFAULT_SIZES.tee).slice();
    var ranks = sizes.map(canonicalRank);
    var smallestRank = Math.min.apply(null, ranks);
    var largestRank = Math.max.apply(null, ranks);
    var smallest = sizes[ranks.indexOf(smallestRank)];
    var largest = sizes[ranks.indexOf(largestRank)];
    var estimate = estimateUsual(category, body);
    var usualRank = canonicalRank(estimate.size);

    // The style's size for a table-size rank. checkLook applies the tee's
    // oversized-look limit (FITS.tee maxUsual).
    function sizeFor(rank, checkLook) {
      var target = rank + fit.offset;
      var needed = SIZE_ORDER[target] || '';
      if (checkLook && fit.maxUsual && rank > canonicalRank(fit.maxUsual) && target <= largestRank) {
        // The size exists but would not give the intended look (the tee on a
        // table 2XL): the look needs the next size, which is not made.
        return { size: null, needed: SIZE_ORDER[target + 1] || '', status: 'look', made: false };
      }
      var at = ranks.indexOf(target);
      if (at > -1) return { size: sizes[at], needed: needed, status: 'exact', made: true };
      if (target < smallestRank) {
        if (smallestRank - target === 1) return { size: smallest, needed: needed, status: 'smallest', made: true };
        return { size: null, needed: needed, status: 'below', made: false };
      }
      return { size: null, needed: needed, status: target > largestRank ? 'above' : 'missing', made: false };
    }

    var base = sizeFor(usualRank, true);
    var chosen = base;
    var sizedUp = false;
    if (estimate.nearUp && !fit.noSizeUp) {
      // Between two sizes: the larger one, when the style is made in it. The
      // tee's look limit is not applied to this step: between XL and 2XL, the
      // 2XL tee still has the room of an oversized fit.
      var up = sizeFor(usualRank + 1, false);
      if (up.made && (!base.made || canonicalRank(up.size) >= canonicalRank(base.size))) {
        sizedUp = !base.made || up.size !== base.size;
        chosen = up;
      }
    }

    return {
      category: category,
      usual: estimate.size,
      usualRank: usualRank,
      estimate: estimate,
      sizes: sizes,
      smallest: smallest,
      largest: largest,
      smallestRank: smallestRank,
      largestRank: largestRank,
      range: smallest + ' to ' + largest,
      fit: fit,
      base: base,
      chosen: chosen,
      sizedUp: sizedUp,
    };
  }

  // Older call shape kept for the sizing tests: (category, body, choice,
  // available, usual). The fit choice and the usual size are ignored.
  function recommendFromUsual(category, body, choice, available) {
    return recommendFor(category, body, available);
  }

  // The words after the size: "for the intended relaxed, oversized fit".
  function tailText(rec) {
    return rec.chosen.status === 'smallest' ? 'the smallest size in this style' : rec.fit.why;
  }

  // Order line note detail: "Recommended M (smallest size in this style)".
  function orderLabel(rec) {
    return rec.chosen.status === 'smallest' ? 'smallest size in this style' : '';
  }

  // Note under a size that is made: the smallest size, or a size that fits
  // closer than the style's intended look.
  function fitNote(rec) {
    var f = rec.chosen;
    if (!f || !f.made) return '';
    if (f.status === 'smallest') return 'It will fit a little roomier than intended.';
    if (!rec.sizedUp && !rec.fit.offset && CLOSE_FIT[rec.category] && CLOSE_FIT[rec.category][canonicalSize(rec.usual)]) {
      return CLOSE_FIT[rec.category][canonicalSize(rec.usual)];
    }
    return '';
  }

  // Why the larger of two sizes was recommended. Not on the smallest size,
  // whose own note already says it fits roomier.
  function betweenNote(rec) {
    return rec.sizedUp && rec.chosen.made && rec.chosen.status !== 'smallest' ? BETWEEN_NOTE : '';
  }

  // Height notes: the size already allows for height, or it will fit a little
  // long. About the garment, never the body.
  function heightNote(rec) {
    var e = rec.estimate;
    if (!rec.chosen.made) return '';
    if (e.tall) return 'This size accounts for your height.';
    if (e.short && rec.category !== 'crop') {
      return rec.category === 'pants'
        ? 'Based on your height, the legs will likely be long on you.'
        : 'Based on your height, it will likely fit a little longer on you.';
    }
    return '';
  }

  // Shown instead of a size when the style is not made in the size needed.
  function notMadeText(rec) {
    var f = rec.chosen;
    if (f.status === 'below') {
      return 'This style starts at ' + rec.smallest + ', ' + sizesText(rec.smallestRank - canonicalRank(f.needed)) +
        " above the size we'd recommend, so it would likely be too big.";
    }
    if (f.status === 'missing') return f.needed + " isn't available in this style.";
    if (f.status === 'look') {
      return 'For the intended ' + rec.fit.name.replace(/ fit$/, '') + " fit you'd need a " + (f.needed || 'bigger size') +
        ', and ' + rec.largest + ' is the largest size we make in this style.';
    }
    return "We'd recommend " + (f.needed || 'a bigger size') + ', and this style comes in ' + rec.range + '.';
  }

  // How the largest size would fit when the size needed is above the range.
  // About the garment, never the body.
  function largestVerdict(rec) {
    var gap = rec.usualRank - rec.largestRank;
    var small = !!RUNS_SMALL[rec.category];
    var look = rec.base.status === 'look' ? rec.fit.shortOfLook : '';
    if (look) return 'Our ' + rec.largest + ' ' + look + '.';
    if (rec.fit.aboveNotes && gap >= 1) return 'Our largest size, ' + rec.largest + ', ' + rec.fit.aboveNotes[Math.min(gap, 2)] + '.';
    if (gap <= 0) {
      return small
        ? 'Our largest size, ' + rec.largest + ', would likely fit snug, since this style runs small.'
        : 'Our largest size, ' + rec.largest + ', would likely fit closer than intended.';
    }
    if (gap === 1 && !small) return 'Our largest size, ' + rec.largest + ', would likely fit tight.';
    return 'Our largest size, ' + rec.largest + ', would likely fit too tight.';
  }

  // Message when no size is recommended: what would be needed, how the
  // largest size would fit when the need is above the range, and the email.
  function notMadeMessage(rec) {
    var f = rec.chosen;
    var text = notMadeText(rec);
    if (f.status === 'above' || f.status === 'look') text += ' ' + largestVerdict(rec);
    return text + ' ' + CONTACT;
  }

  /**
   * category: tee | hoodie | hoodie-essential | crop | tank | tank-snow | pants
   * body: canonical height and weight (canonicalBody).
   * available: the product's Size option values.
   * Returns { size, tail, label, note, message, detail }. size is null when
   * the style is not made in the size needed; message then says why.
   * Older call shapes still work: (category, body, choice, available, usual)
   * and (category, heightIn, weightLb, choice, available, usual).
   */
  function recommend(category, body, available) {
    if (typeof body === 'number') {
      return recommend(category, canonicalBody(body, arguments[2]), arguments[4]);
    }
    if (!Array.isArray(available) && Array.isArray(arguments[3])) available = arguments[3];
    var rec = recommendFor(category, body, Array.isArray(available) ? available : null);
    var f = rec.chosen;
    return {
      size: f.made ? f.size : null,
      tail: f.made ? tailText(rec) : '',
      label: f.made ? orderLabel(rec) : '',
      note: [fitNote(rec), betweenNote(rec), heightNote(rec)].filter(Boolean).join(' '),
      message: f.made ? '' : notMadeMessage(rec),
      detail: rec,
    };
  }

  /* ---- tolerant height / weight parser (typed formats) ---- */
  // Every reader returns an entry that remembers the unit system it was typed
  // in: height { inches, metric } and weight { lb, metric }. canonicalBody()
  // needs the system to round each entry the way its equivalent in the other
  // system would be typed.
  function cleanInput(raw) {
    return String(raw || '')
      .toLowerCase()
      .replace(/[’‘]/g, "'")
      .replace(/[”“]/g, '"')
      .replace(/,/g, '.')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\.$/, '');
  }

  function isBareNumber(s) {
    return /^\d+(?:\.\d+)?$/.test(s);
  }

  // Height entry from free text, or null when it cannot be read.
  function parseHeightEntry(raw) {
    var s = cleanInput(raw);
    if (!s) return null;
    var m;
    // 5'11", 5' 11, 5ft 11in, 5 ft 11, 5feet11, 5f11, 6'
    m = s.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft\.?|feet|foot|f)\s*(\d+(?:\.\d+)?)?\s*(?:"|''|in\.?|inch|inches)?$/);
    if (m) return { inches: parseFloat(m[1]) * 12 + (m[2] ? parseFloat(m[2]) : 0), metric: false };
    // "5 11": two bare numbers
    m = s.match(/^(\d{1,2})\s+(\d{1,2})$/);
    if (m) return { inches: parseInt(m[1], 10) * 12 + parseInt(m[2], 10), metric: false };
    // 180 cm
    m = s.match(/^(\d+(?:\.\d+)?)\s*(?:cm|cms|centimeter|centimeters|centimetre|centimetres)\.?$/);
    if (m) return { inches: parseFloat(m[1]) / CM_PER_IN, metric: true };
    // 1.80 m
    m = s.match(/^(\d+(?:\.\d+)?)\s*(?:m|meter|meters|metre|metres)\.?$/);
    if (m) return { inches: (parseFloat(m[1]) * 100) / CM_PER_IN, metric: true };
    // 71 in, 71"
    m = s.match(/^(\d+(?:\.\d+)?)\s*(?:"|''|in\.?|inch|inches)$/);
    if (m) return { inches: parseFloat(m[1]), metric: false };
    // bare number
    m = s.match(/^(\d+)(?:\.(\d+))?$/);
    if (m) {
      var n = parseFloat(s);
      if (n < 3) return { inches: (n * 100) / CM_PER_IN, metric: true }; // meters
      if (n <= 8) {
        // feet; the decimals are inches (5.11 = 5 ft 11 in, 5.5 = 5 ft 5 in)
        var ft = parseInt(m[1], 10);
        var frac = m[2] || '';
        var inch = 0;
        if (frac) {
          if (frac.length <= 2 && parseInt(frac, 10) <= 11) inch = parseInt(frac, 10);
          else inch = Math.round(parseFloat('0.' + frac) * 12);
        }
        return { inches: ft * 12 + inch, metric: false };
      }
      if (n >= 50 && n <= 100) return { inches: n, metric: false }; // inches
      if (n > 100) return { inches: n / CM_PER_IN, metric: true }; // cm
      return null;
    }
    return null;
  }

  // Weight entry from free text, or null when it cannot be read.
  function parseWeightEntry(raw) {
    var s = cleanInput(raw);
    if (!s) return null;
    var m;
    m = s.match(/^(\d+(?:\.\d+)?)\s*(?:kg|kgs|kilo|kilos|kilogram|kilograms|kilogramme|kilogrammes)\.?$/);
    if (m) return { lb: parseFloat(m[1]) * LB_PER_KG, metric: true };
    m = s.match(/^(\d+(?:\.\d+)?)\s*(?:lb|lbs|pound|pounds)\.?$/);
    if (m) return { lb: parseFloat(m[1]), metric: false };
    m = s.match(/^(\d+)\s*(?:st|stone)\s*(\d+(?:\.\d+)?)?\s*(?:lb|lbs)?\.?$/);
    if (m) return { lb: parseInt(m[1], 10) * 14 + (m[2] ? parseFloat(m[2]) : 0), metric: false };
    m = s.match(/^(\d+(?:\.\d+)?)$/);
    if (m) {
      var n = parseFloat(m[1]);
      return n < 130 ? { lb: n * LB_PER_KG, metric: true } : { lb: n, metric: false };
    }
    return null;
  }

  // Inches (or pounds), or null when the value cannot be read.
  function parseHeight(raw) {
    var e = parseHeightEntry(raw);
    return e ? e.inches : null;
  }

  function parseWeight(raw) {
    var e = parseWeightEntry(raw);
    return e ? e.lb : null;
  }

  /* ---- unit-aware readers (the v2 inputs) ---- */

  // Height typed in the cm field. Bare numbers: under 3 = meters (1.80), 3 to
  // under 10 = feet with decimal inches (5.11), 100 and over = centimeters.
  // 10-99 is ambiguous (inches or a typo) and is rejected with a clear message.
  // Typed units (5'11, 180cm, 71 in) are still understood. inches is NaN when
  // the text cannot be read, and the entry is null when the field is empty.
  function heightEntryFromCm(raw) {
    var s = cleanInput(raw);
    if (!s) return null;
    if (isBareNumber(s)) {
      var n = parseFloat(s);
      if (n < 3) return { inches: (n * 100) / CM_PER_IN, metric: true };
      if (n < 10) return parseHeightEntry(s);
      if (n < 100) return { inches: NaN, metric: true };
      return { inches: n / CM_PER_IN, metric: true };
    }
    return parseHeightEntry(s) || { inches: NaN, metric: true };
  }

  // Height from the ft and in fields. "5.11" or "5'11" typed in the feet field
  // with the inches field empty is read as 5 ft 11 in. Inches only (71) is read
  // as total inches when it is 12 or more.
  function heightEntryFromFtIn(ftRaw, inRaw) {
    var f = cleanInput(ftRaw);
    var i = cleanInput(inRaw);
    if (!f && !i) return null;
    if (f && !i && !/^\d+$/.test(f)) return parseHeightEntry(f) || { inches: NaN, metric: false };
    var ft = f ? parseFloat(f) : 0;
    var inch = i ? parseFloat(i) : 0;
    if (isNaN(ft) || isNaN(inch)) return { inches: NaN, metric: false };
    if (!f) return { inches: inch >= 12 ? inch : NaN, metric: false };
    return { inches: ft * 12 + inch, metric: false };
  }

  // Weight from the single field. Bare numbers follow the selected unit; a typed
  // unit (187 lb in the kg field) wins.
  function weightEntryFrom(raw, unit) {
    var s = cleanInput(raw);
    if (!s) return null;
    if (isBareNumber(s)) {
      var n = parseFloat(s);
      return unit === 'kg' ? { lb: n * LB_PER_KG, metric: true } : { lb: n, metric: false };
    }
    return parseWeightEntry(s) || { lb: NaN, metric: unit === 'kg' };
  }

  // Number-only forms (console and older callers).
  function heightFromCm(raw) {
    var e = heightEntryFromCm(raw);
    return e ? e.inches : null;
  }

  function heightFromFtIn(ftRaw, inRaw) {
    var e = heightEntryFromFtIn(ftRaw, inRaw);
    return e ? e.inches : null;
  }

  function weightFrom(raw, unit) {
    var e = weightEntryFrom(raw, unit);
    return e ? e.lb : null;
  }

  function isHalfStep(value, steps) {
    return Math.abs(value * steps - Math.round(value * steps)) < 1e-6;
  }

  function feetInches(heightIn) {
    var totalIn = Math.round(heightIn);
    return { ft: Math.floor(totalIn / 12), inch: totalIn % 12 };
  }

  // "5'11" (180 cm)" or "180 cm (5'11")", leading with the unit in use. A half
  // inch typed in the inches field stays visible (5'10.5"), and so does a
  // decimal typed in cm (178.5 cm).
  function describeHeight(entry, unit) {
    var inches = entry.inches;
    var half = !entry.metric && isHalfStep(inches, 2) && !isHalfStep(inches, 1);
    var imperial;
    if (half) {
      var ft = Math.floor(inches / 12);
      imperial = ft + "'" + (inches - ft * 12) + '"';
    } else {
      var fi = feetInches(inches);
      imperial = fi.ft + "'" + fi.inch + '"';
    }
    var cm = entry.metric ? Math.round(inches * CM_PER_IN * 10) / 10 : Math.round(inches * CM_PER_IN);
    var metric = cm + ' cm';
    return unit === 'cm' ? metric + ' (' + imperial + ')' : imperial + ' (' + metric + ')';
  }

  // "187 lb (85 kg)" or "85 kg (187 lb)". A decimal stays visible in the unit
  // it was typed in (72.5 kg, 160.4 lb).
  function describeWeight(entry, unit) {
    var lb = entry.metric ? Math.round(entry.lb) : Math.round(entry.lb * 10) / 10;
    var kg = entry.metric ? Math.round((entry.lb / LB_PER_KG) * 10) / 10 : Math.round(entry.lb / LB_PER_KG);
    return unit === 'kg' ? kg + ' kg (' + lb + ' lb)' : lb + ' lb (' + kg + ' kg)';
  }

  // Legacy single-field forms: "5'11\" (180 cm) / 187 lb (85 kg)"
  function describeInputs(height, weight) {
    return describeHeight(height, 'ft') + ' / ' + describeWeight(weight, 'lb');
  }

  /* ---- units, remembered per browser ---- */
  var UNITS_KEY = 'cro-ff-units';
  var units = { height: 'ft', weight: 'lb' };
  try {
    var savedUnits = JSON.parse(window.localStorage.getItem(UNITS_KEY) || 'null');
    if (savedUnits && (savedUnits.height === 'cm' || savedUnits.height === 'ft')) units.height = savedUnits.height;
    if (savedUnits && (savedUnits.weight === 'kg' || savedUnits.weight === 'lb')) units.weight = savedUnits.weight;
  } catch (e) {
    /* storage unavailable: defaults apply */
  }

  function saveUnits() {
    try {
      window.localStorage.setItem(UNITS_KEY, JSON.stringify(units));
    } catch (e) {
      /* ignore */
    }
  }

  function initSizeGuide() {
    document.querySelectorAll('[data-cro-size-guide]').forEach(function (guide) {
      // Unit toggle: switches every measurement table inside this container.
      var tables = guide.querySelectorAll('.cro-sg__table');
      var unitNotes = guide.querySelectorAll('[data-cro-unit-note]');
      guide.querySelectorAll('[data-cro-unit]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var unit = btn.getAttribute('data-cro-unit');
          tables.forEach(function (table) { table.setAttribute('data-unit', unit); });
          guide.querySelectorAll('[data-cro-unit]').forEach(function (b) {
            b.classList.toggle('is-active', b.getAttribute('data-cro-unit') === unit);
          });
          unitNotes.forEach(function (note) {
            note.textContent = unit === 'cm' ? 'Garment measurements in centimeters, laid flat.' : 'Garment measurements in inches, laid flat.';
          });
        });
      });

      guide.querySelectorAll('[data-cro-ff]').forEach(function (finder) {
        initFitFinder(finder, guide);
      });
    });
  }

  function initFitFinder(finder, guide) {
    var category = finder.getAttribute('data-category') || 'tee';
    var available = (finder.getAttribute('data-sizes') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    var sectionId = finder.getAttribute('data-section');

    // v2 inputs (snippets/cro-fit-inputs.liquid)
    var cmInput = finder.querySelector('[data-ff-height-cm-value]');
    var ftInput = finder.querySelector('[data-ff-height-ft-value]');
    var inInput = finder.querySelector('[data-ff-height-in-value]');
    var weightValue = finder.querySelector('[data-ff-weight-value]');
    var weightSuffix = finder.querySelector('[data-ff-weight-suffix]');
    var cmWrap = finder.querySelector('[data-ff-height-cm]');
    var ftWrap = finder.querySelector('[data-ff-height-ft]');
    var unitRadios = Array.from(finder.querySelectorAll('[data-ff-unit]'));
    var hasUnits = !!(cmInput && ftInput && inInput && weightValue && unitRadios.length);

    // Legacy single-field inputs (kept so an older template still works)
    var heightInput = finder.querySelector('[data-ff-height]');
    var weightInput = finder.querySelector('[data-ff-weight]');

    var result = finder.querySelector('[data-ff-result]');
    var resultSize = finder.querySelector('[data-ff-result-size]');
    var resultTail = finder.querySelector('[data-ff-result-tail]');
    var resultList = finder.querySelector('[data-ff-result-list]');
    var resultNote = finder.querySelector('[data-ff-result-note]');
    var readback = finder.querySelector('[data-ff-readback]');
    var useButton = finder.querySelector('[data-ff-use]');
    var error = finder.querySelector('[data-ff-error]');
    // The "Recommended: ..." line (hidden when no size is recommended) and the
    // message shown instead.
    var resultLine = finder.querySelector('[data-ff-result-line]');
    var resultMessage = finder.querySelector('[data-ff-result-message]');
    var lastRecommendation = null;
    var lastLabel = '';
    var lastShown = null; // the recommendation on screen (product pages)

    /* ---- units ---- */
    // changed: 'height' | 'weight' when the customer switched a unit (the value
    // typed under the old unit is converted so they see it carried over), or
    // null on first paint.
    function applyUnits(changed) {
      if (!hasUnits) return;
      unitRadios.forEach(function (radio) {
        var kind = radio.getAttribute('data-ff-unit');
        radio.checked = radio.value === units[kind];
      });

      if (changed === 'height') {
        // The value carries over as its equivalent, rounded to whole units
        // (178 cm <-> 5'10"), which gives the same recommendation.
        if (units.height === 'ft') {
          var h = heightEntryFromCm(cmInput.value);
          if (h && !isNaN(h.inches)) {
            var fi = feetInches(h.inches);
            ftInput.value = String(fi.ft);
            inInput.value = String(fi.inch);
          }
        } else {
          var h2 = heightEntryFromFtIn(ftInput.value, inInput.value);
          if (h2 && !isNaN(h2.inches)) cmInput.value = String(Math.round(h2.inches * CM_PER_IN));
        }
      }
      if (changed === 'weight') {
        var previous = weightValue.getAttribute('data-unit') || 'lb';
        var typed = cleanInput(weightValue.value);
        if (previous !== units.weight && typed && isBareNumber(typed)) {
          var n = parseFloat(typed);
          weightValue.value = String(Math.round(units.weight === 'kg' ? n / LB_PER_KG : n * LB_PER_KG));
        }
      }

      cmWrap.hidden = units.height !== 'cm';
      ftWrap.hidden = units.height !== 'ft';
      weightValue.setAttribute('data-unit', units.weight);
      weightValue.placeholder = units.weight === 'kg' ? '85' : '187';
      weightValue.setAttribute('aria-label', units.weight === 'kg' ? 'Weight in kilograms' : 'Weight in pounds');
      if (weightSuffix) weightSuffix.textContent = units.weight;

      if (changed === 'height') (units.height === 'cm' ? cmInput : ftInput).focus();
      if (changed === 'weight') weightValue.focus();
      updateReadback();
    }

    if (hasUnits) {
      unitRadios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          var kind = radio.getAttribute('data-ff-unit');
          if (units[kind] === radio.value) return;
          units[kind] = radio.value;
          saveUnits();
          applyUnits(kind);
        });
      });
      [cmInput, ftInput, inInput, weightValue].forEach(function (input) {
        input.addEventListener('input', updateReadback);
      });
      applyUnits(null);
    }

    /* ---- reading the fields ---- */
    // Returns { value, entry, error }: value in inches (null = empty, NaN =
    // unreadable) and entry = { inches, metric } for canonicalBody().
    function readHeight() {
      var entry;
      if (hasUnits) {
        entry = units.height === 'cm' ? heightEntryFromCm(cmInput.value) : heightEntryFromFtIn(ftInput.value, inInput.value);
      } else {
        entry = parseHeightEntry(heightInput ? heightInput.value : '');
      }
      if (entry === null) return { value: null, entry: null, error: 'Enter your height.' };
      var value = entry.inches;
      if (isNaN(value) || value < HEIGHT_MIN_IN || value > HEIGHT_MAX_IN) {
        var msg;
        if (!hasUnits) msg = "We couldn't read the height. Try 180 cm, 1.80, 5'11 or 71 in.";
        else if (units.height === 'cm') msg = 'Height in cm should be between 122 and 229. Using feet? Switch to ft / in.';
        else msg = 'Height should be between 4 ft 0 in and 7 ft 6 in. Using centimeters? Switch to cm.';
        return { value: NaN, entry: entry, error: msg };
      }
      return { value: value, entry: entry, error: '' };
    }

    // Returns { value, entry, error }: value in pounds and entry = { lb, metric }.
    function readWeight() {
      var entry;
      if (hasUnits) entry = weightEntryFrom(weightValue.value, units.weight);
      else entry = parseWeightEntry(weightInput ? weightInput.value : '');
      if (entry === null) return { value: null, entry: null, error: 'Enter your weight.' };
      var value = entry.lb;
      if (isNaN(value) || value < WEIGHT_MIN_LB || value > WEIGHT_MAX_LB) {
        var msg;
        if (!hasUnits) msg = "We couldn't read the weight. Try 85 kg or 187 lb.";
        else if (units.weight === 'kg') msg = 'Weight in kg should be between 28 and 204. Using pounds? Switch to lb.';
        else msg = 'Weight in lb should be between 60 and 450. Using kilos? Switch to kg.';
        return { value: NaN, entry: entry, error: msg };
      }
      return { value: value, entry: entry, error: '' };
    }

    // Live line under the fields: "Read as 5'11" (180 cm), 187 lb (85 kg)".
    function updateReadback() {
      if (!readback || !hasUnits) return;
      var h = readHeight();
      var w = readWeight();
      var parts = [];
      if (h.value !== null && !isNaN(h.value)) parts.push(describeHeight(h.entry, units.height));
      if (w.value !== null && !isNaN(w.value)) parts.push(describeWeight(w.entry, units.weight));
      if (!parts.length) {
        readback.hidden = true;
        readback.textContent = '';
        return;
      }
      readback.textContent = 'Read as ' + parts.join(', ');
      readback.hidden = false;
    }

    /* ---- the product's Size picker ---- */
    function sizeFieldsets() {
      var picker = sectionId ? document.querySelector('#variant-selects-' + sectionId) : document.querySelector('variant-selects');
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('.product-form__input')).filter(function (fieldset) {
        var legend = fieldset.querySelector('legend, label.form__label');
        var name = legend ? legend.textContent.trim().toLowerCase() : '';
        return name.indexOf('size') === 0;
      });
    }

    function selectedSize() {
      var checked = null;
      sizeFieldsets().forEach(function (fieldset) {
        var radio = fieldset.querySelector('input[type="radio"]:checked');
        if (radio) checked = radio.value;
        var select = fieldset.querySelector('select');
        if (select) checked = select.value;
      });
      return checked || '';
    }

    // True when the theme marks this size sold out or unavailable for the
    // options selected now (Dawn adds class "disabled" to the size's radio).
    function sizeSoldOut(size) {
      var soldOut = false;
      sizeFieldsets().forEach(function (fieldset) {
        Array.from(fieldset.querySelectorAll('input[type="radio"]')).forEach(function (radio) {
          if (radio.value === size && radio.classList.contains('disabled')) soldOut = true;
        });
        var select = fieldset.querySelector('select');
        if (select) {
          Array.from(select.options).forEach(function (option) {
            if (option.value === size && /unavailable|sold out/i.test(option.textContent)) soldOut = true;
          });
        }
      });
      return soldOut;
    }

    /* ---- line-item property ---- */
    function propertyInputs() {
      return Array.from(document.querySelectorAll('[data-cro-ff-property]'));
    }

    function writeProperty() {
      if (!lastRecommendation || category === 'all') return;
      var ordered = selectedSize();
      propertyInputs().forEach(function (input) {
        input.disabled = false;
        input.value = 'Recommended ' + lastRecommendation + (lastLabel ? ' (' + lastLabel + ')' : '') + ' · ordered ' + (ordered || 'n/a');
      });
    }

    // When the latest result recommends no size, an earlier recommendation
    // must not ride along with the order.
    function clearProperty() {
      if (category === 'all') return;
      propertyInputs().forEach(function (input) {
        input.value = '';
        input.disabled = true;
      });
    }

    if (category !== 'all') {
      onVariantChange(function () {
        window.setTimeout(function () {
          writeProperty();
          showSoldOut();
        }, 0);
      });
      document.addEventListener('submit', function (event) {
        if (event.target && event.target.getAttribute('data-type') === 'add-to-cart-form') writeProperty();
      }, true);
    }

    function showError(message) {
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
      if (result) result.hidden = true;
    }

    function setMessage(text) {
      if (!resultMessage) return;
      resultMessage.textContent = text;
      resultMessage.hidden = !text;
    }

    // The recommended size is right but sold out in the selected colour:
    // say so and hide the button, so the size is never presented as one to
    // buy now. Re-checked whenever the customer changes a variant.
    function showSoldOut() {
      if (!lastShown || !lastShown.chosen.made || !result || result.hidden) return;
      var size = lastShown.chosen.size;
      var soldOut = sizeSoldOut(size);
      setMessage(soldOut ? size + ' is sold out in this color right now. Choose another color, or email hello@empiricwear.com.' : '');
      if (useButton) useButton.hidden = soldOut;
    }

    /* ---- product pages: one recommendation ---- */
    // The recommended size with any note. When the style is not made in the
    // size needed, a message instead of a size (never the largest or smallest
    // size as a stand-in) and no button.
    function showResult(rec) {
      var chosen = rec.chosen;
      var note = [fitNote(rec), betweenNote(rec), heightNote(rec)].filter(Boolean).join(' ');
      lastShown = rec;
      if (resultNote) {
        resultNote.textContent = note;
        resultNote.hidden = !note;
      }

      if (chosen.made) {
        lastRecommendation = chosen.size;
        lastLabel = orderLabel(rec);
        if (resultLine) resultLine.hidden = false;
        if (resultSize) resultSize.textContent = chosen.size;
        if (resultTail) resultTail.textContent = tailText(rec);
        setMessage('');
        if (useButton) {
          useButton.hidden = false;
          useButton.textContent = 'Select ' + chosen.size;
        }
        if (result) result.hidden = false;
        writeProperty();
        showSoldOut();
        return;
      }

      lastRecommendation = null;
      lastLabel = '';
      clearProperty();
      if (resultLine) resultLine.hidden = true;
      setMessage(notMadeMessage(rec));
      if (useButton) useButton.hidden = true;
      if (result) result.hidden = false;
    }

    /* ---- size guide page: one row per category ---- */
    function pageRow(cat, rec) {
      var li = document.createElement('li');
      var label = document.createElement('span');
      label.className = 'cro-ff__cat';
      label.textContent = cat.label;
      li.appendChild(label);
      var chosen = rec.chosen;
      // The size and its tail share one line box, so the comma sits right
      // after the size and a long tail wraps under it.
      var tail = document.createElement('span');
      tail.className = 'cro-ff__tail';
      if (chosen.made) {
        var value = document.createElement('strong');
        value.textContent = chosen.size;
        tail.appendChild(value);
        tail.appendChild(document.createTextNode(', ' + tailText(rec)));
      } else {
        var verdict = chosen.status === 'above' || chosen.status === 'look';
        tail.textContent = notMadeText(rec) + (verdict ? ' ' + largestVerdict(rec) : '');
      }
      li.appendChild(tail);
      var line = chosen.made ? fitNote(rec) : '';
      if (line) {
        var alt = document.createElement('span');
        alt.className = 'cro-ff__alt';
        alt.textContent = line;
        li.appendChild(alt);
      }
      return li;
    }

    // The note under the size guide page list. unisex: the tee row's
    // recommendation, for the height notes.
    function pageNote(unisex, anySizedUp, anyNotMade) {
      var parts = ['Sizes are based on your height and weight.'];
      var e = unisex.estimate;
      if (anySizedUp) parts.push("Where you're close to the line between two sizes, we show the larger one.");
      if (e.tall) parts.push('They account for your height.');
      if (e.short) parts.push('Based on your height, most pieces will likely fit a little longer on you.');
      if (anyNotMade) parts.push(CONTACT);
      return parts.join(' ');
    }

    /* ---- submit ---- */
    function run() {
      if (error) error.hidden = true;

      var h = readHeight();
      var w = readWeight();
      if (h.value === null && w.value === null) {
        return showError('Enter your height and weight to get a recommendation.');
      }
      if (h.error) return showError(h.error);
      if (w.error) return showError(w.error);
      // One set of numbers for every unit system (see canonicalBody).
      var body = canonicalBody(h.entry, w.entry);

      if (readback) {
        readback.textContent = hasUnits
          ? 'Read as ' + describeHeight(h.entry, units.height) + ', ' + describeWeight(w.entry, units.weight)
          : 'Read as ' + describeInputs(h.entry, w.entry);
        readback.hidden = false;
      }

      if (category === 'all') {
        // Size guide page: one recommended size per style.
        var anyNotMade = false;
        var anySizedUp = false;
        if (resultList) {
          resultList.innerHTML = '';
          ALL_CATEGORIES.forEach(function (cat) {
            var rec = recommendFor(cat.key, body, null);
            if (!rec.chosen.made) anyNotMade = true;
            if (betweenNote(rec)) anySizedUp = true;
            resultList.appendChild(pageRow(cat, rec));
          });
        }
        if (resultNote) {
          resultNote.textContent = pageNote(recommendFor('tee', body, null), anySizedUp, anyNotMade);
          resultNote.hidden = false;
        }
        if (result) result.hidden = false;
        return;
      }

      showResult(recommendFor(category, body, available));
    }

    finder.addEventListener('submit', function (event) {
      event.preventDefault();
      run();
    });

    if (useButton) {
      useButton.addEventListener('click', function () {
        if (!lastRecommendation) return;
        sizeFieldsets().forEach(function (fieldset) {
          var radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(function (r) {
            return r.value === lastRecommendation;
          });
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          var select = fieldset.querySelector('select');
          if (select && select.value !== lastRecommendation) {
            select.value = lastRecommendation;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        var modal = guide ? guide.closest('modal-dialog') : null;
        if (modal && typeof modal.hide === 'function') modal.hide();
      });
    }
  }

  /* ---- "Find my size": open the size guide at the Fit Finder ---- */
  // Any element with data-cro-ff-jump: the product page entry under the fit
  // line and the Fit Promise link (both inside a <modal-opener>, which opens
  // the modal first), and the "Find my size" button at the top of the modal.
  // The "Size guide & Fit Finder" link opens the modal at the top.
  function fitFinderModal(trigger) {
    var modal = trigger.closest('modal-dialog');
    if (modal) return modal;
    var opener = trigger.closest('modal-opener');
    var selector = opener ? opener.getAttribute('data-modal') : '';
    return selector ? document.querySelector(selector) : null;
  }

  function jumpToFitFinder(modal) {
    var scroller = modal.querySelector('.product-popup-modal__content');
    var target = modal.querySelector('[data-cro-ff-anchor]');
    if (!scroller || !target) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Land the heading just below the sticky close button, so the button does
    // not cover the first line of the Fit Finder.
    var gap = 8;
    var close = modal.querySelector('.product-popup-modal__toggle');
    if (close && window.getComputedStyle(close).position === 'sticky') {
      gap = (parseFloat(window.getComputedStyle(close).top) || 0) + close.offsetHeight + 8;
    }
    var top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - gap;
    if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    else scroller.scrollTop = Math.max(0, top);
    // Keyboard and mouse users land in the height field. On touch screens the
    // heading takes focus instead, so the keyboard does not cover the form.
    var fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var field = fine ? modal.querySelector('[data-cro-ff] .cro-ff__inputs:not([hidden]) input') : null;
    var focusTarget = field || target;
    try {
      focusTarget.focus({ preventScroll: true });
    } catch (e) {
      focusTarget.focus();
    }
  }

  function initFitFinderLinks() {
    document.addEventListener('click', function (event) {
      var trigger = event.target && event.target.closest ? event.target.closest('[data-cro-ff-jump], .cro-size-guide-link') : null;
      if (!trigger) return;
      var modal = fitFinderModal(trigger);
      if (!modal || !modal.querySelector('[data-cro-ff-anchor]')) return;
      var jump = trigger.hasAttribute('data-cro-ff-jump');
      // Let <modal-opener> open the dialog (and move focus into it) first.
      window.setTimeout(function () {
        if (jump) {
          jumpToFitFinder(modal);
        } else {
          var scroller = modal.querySelector('.product-popup-modal__content');
          if (scroller) scroller.scrollTop = 0;
        }
      }, 60);
    });
  }

  // Exposed for debugging in the console and for the sizing tests.
  window.croFitFinder = {
    recommend: recommend,
    recommendFor: recommendFor,
    recommendFromUsual: recommendFromUsual,
    estimateUsual: estimateUsual,
    canonicalHeightIn: canonicalHeightIn,
    canonicalWeightKg: canonicalWeightKg,
    canonicalBody: canonicalBody,
    parseHeight: parseHeight,
    parseHeightEntry: parseHeightEntry,
    parseWeightEntry: parseWeightEntry,
    heightEntryFromCm: heightEntryFromCm,
    heightEntryFromFtIn: heightEntryFromFtIn,
    weightEntryFrom: weightEntryFrom,
    describeHeight: describeHeight,
    describeWeight: describeWeight,
    parseWeight: parseWeight,
    heightFromCm: heightFromCm,
    heightFromFtIn: heightFromFtIn,
    weightFrom: weightFrom,
    fitNote: fitNote,
    betweenNote: betweenNote,
    heightNote: heightNote,
    notMadeMessage: notMadeMessage,
    tailText: tailText,
    orderLabel: orderLabel,
  };

  /* -------------------------------------- 6. same principle, other builds */
  var HAT_TITLE = /\b(hat|hats|cap|caps)\b/i;

  // "DROP 001 — STRESS BUILDS." -> "stress builds"
  // "Not Fragile — Snow Washed Hoodie" -> "not fragile"
  // "LOAD → ADAPT → RECOVER." -> "load adapt recover"
  // "STRESS BUILDS. Essential Heavyweight Hoodie" -> "stress builds"
  function designName(title) {
    var t = String(title || '');
    t = t.replace(/^\s*drop\s*\d+\s*[—–-]?\s*/i, '');
    // "Snow Washed Frayed Hem Tank Top - MIND IS BIOLOGY": build first, design after the dash.
    var dashParts = t.split(/\s+[—–-]\s+/);
    if (dashParts.length > 1 && /tank top|frayed hem/i.test(dashParts[0])) t = dashParts.slice(1).join(' ');
    t = t.split(/\s+[—–-]\s+/)[0];
    t = t.split(/\.\s+/)[0];
    t = t.toLowerCase().replace(/[^a-z0-9\s]+/g, ' ').replace(/\s+/g, ' ').trim();
    return t;
  }

  function buildRank(product) {
    var title = String(product.title || '');
    var type = String(product.type || '');
    if (/crop\s*tee/i.test(title)) return 1;
    if (/hoodie/i.test(title) || /hoodie/i.test(type)) return 2;
    if (/tank/i.test(title) || /tank/i.test(type)) return 3;
    if (/sweatpants/i.test(title) || /sweatpants/i.test(type)) return 4;
    return 0; // tee
  }

  function garmentType(product) {
    var title = product.title || '';
    if (/women'?s crop tee/i.test(title)) return "Women's Crop Tee";
    if (/essential heavyweight hoodie/i.test(title)) return 'Essential Heavyweight Hoodie';
    if (/snow washed hoodie/i.test(title)) return 'Snow Washed Hoodie';
    if (/ribbed racerback tank/i.test(title)) return 'Ribbed Racerback Tank';
    if (/frayed hem tank/i.test(title)) return 'Snow Washed Tank';
    if (/snow washed sweatpants/i.test(title)) return 'Snow Washed Sweatpants';
    if (/sweatpants/i.test(title)) return 'Sweatpants';
    if (/crop/i.test(title)) return 'Crop Tee';
    if (/hoodie/i.test(title)) return 'Hoodie';
    if (/tank/i.test(title)) return 'Tank';
    var type = product.type || '';
    if (/t-?shirt/i.test(type)) return '190 GSM Tee';
    return type || 'Other build';
  }

  function formatMoney(amount) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var value = typeof amount === 'number' && !isNaN(amount) ? amount.toFixed(2) : String(amount);
    return currency === 'USD' ? '$' + value : value + ' ' + currency;
  }

  function suggest(query) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url =
      root +
      'search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        return (data && data.resources && data.resources.results && data.resources.results.products) || [];
      })
      .catch(function () { return []; });
  }

  function initSamePrinciple() {
    document.querySelectorAll('[data-cro-sp]').forEach(function (module) {
      // Rendered server-side from the design tag: nothing to do.
      if (module.hasAttribute('data-static')) return;

      var title = module.getAttribute('data-title') || '';
      var handle = module.getAttribute('data-handle') || '';
      var design = designName(title);
      var needle = normalize(design);
      if (!design || needle.length < 3) return;

      var words = design.split(' ');
      var queries = [design];
      if (words.length > 2) queries.push(words.slice(0, 2).join(' '));

      Promise.all(queries.map(suggest)).then(function (lists) {
        var seen = {};
        var candidates = [];
        lists.forEach(function (list) {
          list.forEach(function (p) {
            if (!p || !p.handle || seen[p.handle]) return;
            seen[p.handle] = true;
            candidates.push(p);
          });
        });

        var matches = candidates.filter(function (p) {
          if (p.handle === handle) return false;
          if (p.available === false) return false;
          if (HAT_TITLE.test(p.title || '')) return false;
          if (/^hats?$/i.test(String(p.type || '').trim())) return false;
          var candidateDesign = normalize(designName(p.title));
          if (!candidateDesign) return false;
          return (
            candidateDesign === needle ||
            candidateDesign.indexOf(needle) !== -1 ||
            normalize(p.title).indexOf(needle) !== -1
          );
        });

        matches.sort(function (a, b) {
          var diff = buildRank(a) - buildRank(b);
          if (diff !== 0) return diff;
          return String(a.title).localeCompare(String(b.title));
        });

        // One tile per build. Designs that exist in four builds (tee, crop, two
        // hoodies, tank) show all four so the tank is never dropped.
        matches = matches.slice(0, 4);
        if (!matches.length) return;

        var grid = module.querySelector('.cro-sp__grid');
        grid.innerHTML = '';
        grid.setAttribute('data-count', String(matches.length));
        matches.forEach(function (p) {
          var li = document.createElement('li');
          var a = document.createElement('a');
          a.className = 'cro-sp__tile';
          a.href = p.url;
          var media = document.createElement('span');
          media.className = 'cro-sp__media';
          if (p.image || p.featured_image) {
            var img = document.createElement('img');
            var src = p.image || p.featured_image;
            img.src = src.indexOf('?') === -1 ? src + '?width=360' : src + '&width=360';
            img.alt = p.title;
            img.loading = 'lazy';
            img.width = 360;
            img.height = 360;
            media.appendChild(img);
          }
          var type = document.createElement('span');
          type.className = 'cro-sp__type';
          type.textContent = garmentType(p);
          var price = document.createElement('span');
          price.className = 'cro-sp__price';
          price.textContent = formatMoney(parseFloat(p.price));
          a.appendChild(media);
          a.appendChild(type);
          a.appendChild(price);
          li.appendChild(a);
          grid.appendChild(li);
        });
        module.hidden = false;
      });
    });
  }

  /* ------------------------------------------------- 7. complete the set */
  // Sets launch (2026-09-25). Markup: snippets/cro-complete-the-set.liquid.
  // The partner list (matching sweatpants for a hoodie, matching hoodie(s)
  // for sweatpants) is embedded as JSON. The partner's colour follows the
  // colour selected on the main product when the partner has it ("Black
  // Beauty" hoodie -> "Black" sweatpants), the size follows the main size
  // (capped at the partner's largest size), and both can be changed with the
  // pills. "Add both to cart" posts the two lines to /cart/add.js with the
  // cart notification sections, then opens the notification showing both.
  function colorMatches(a, b) {
    var x = normalize(a);
    var y = normalize(b);
    if (!x || !y) return false;
    return x === y || x.indexOf(y) !== -1 || y.indexOf(x) !== -1;
  }

  function uniqueValues(list) {
    var seen = {};
    var out = [];
    list.forEach(function (v) {
      var key = String(v || '');
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push(key);
    });
    return out;
  }

  function readSelectedVariant(sectionId) {
    var el = document.querySelector('#variant-selects-' + sectionId + ' [data-selected-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initCompleteTheSet() {
    document.querySelectorAll('[data-cro-set]').forEach(function (module) {
      var dataEl = module.querySelector('[data-set-partners]');
      if (!dataEl) return;
      var partners;
      try {
        partners = JSON.parse(dataEl.textContent);
      } catch (e) {
        module.hidden = true;
        return;
      }
      partners = (partners || []).filter(function (p) {
        return p && p.variants && p.variants.length;
      });
      if (!partners.length) {
        module.hidden = true;
        return;
      }

      var sectionId = module.getAttribute('data-section');
      var formId = module.getAttribute('data-form');
      var mainColorIdx = parseInt(module.getAttribute('data-color-index'), 10);
      var mainSizeIdx = parseInt(module.getAttribute('data-size-index'), 10);
      var setLive = module.getAttribute('data-set-live') === 'true';
      var saving = parseFloat(module.getAttribute('data-saving')) || 0;
      var partnerWord = module.getAttribute('data-partner-word') || 'piece';

      var partnerList = module.querySelector('[data-set-partner-list]');
      var image = module.querySelector('[data-set-image]');
      var link = module.querySelector('[data-set-link]');
      var titleLink = module.querySelector('[data-set-title-link]');
      var colorsWrap = module.querySelector('[data-set-colors]');
      var colorPills = module.querySelector('[data-set-color-pills]');
      var sizePills = module.querySelector('[data-set-size-pills]');
      var offerPrice = module.querySelector('[data-set-offer-price]');
      var offerCompare = module.querySelector('[data-set-offer-compare]');
      var offerBadge = module.querySelector('[data-set-offer-badge]');
      var button = module.querySelector('[data-set-add]');
      var note = module.querySelector('[data-set-note]');
      var buttonLabel = button ? button.textContent : 'Add both to cart';

      var state = {
        partner: partners[0],
        color: null,
        size: null,
        userColor: false,
        userSize: false,
        main: readSelectedVariant(sectionId),
        sizeNote: '',
      };

      function mainOption(idx) {
        var main = state.main;
        if (!main || idx < 0) return null;
        if (main.options && main.options.length > idx) return main.options[idx];
        var key = 'option' + (idx + 1);
        return main[key] || null;
      }

      function partnerColors(p) {
        if (p.colorIndex < 0) return [];
        return uniqueValues(p.variants.map(function (v) { return v.color; }));
      }

      function partnerSizes(p, color) {
        return uniqueValues(
          p.variants
            .filter(function (v) { return p.colorIndex < 0 || !color || v.color === color; })
            .map(function (v) { return v.size; })
        );
      }

      function findVariant(p, color, size) {
        var matches = p.variants.filter(function (v) {
          if (p.colorIndex >= 0 && color && v.color !== color) return false;
          if (p.sizeIndex >= 0 && size && v.size !== size) return false;
          return true;
        });
        return matches[0] || null;
      }

      function chooseDefaults() {
        var p = state.partner;
        var mainColor = mainOption(mainColorIdx);
        var mainSize = mainOption(mainSizeIdx);
        var colors = partnerColors(p);
        if (colors.length) {
          var keep = state.userColor && colors.indexOf(state.color) !== -1;
          if (!keep) {
            var match = colors.filter(function (c) { return colorMatches(c, mainColor); })[0];
            state.color = match || colors[0];
          }
        } else {
          state.color = null;
        }
        var sizes = partnerSizes(p, state.color);
        state.sizeNote = '';
        if (!sizes.length) {
          state.size = null;
          return;
        }
        var keepSize = state.userSize && sizes.indexOf(state.size) !== -1;
        if (keepSize) return;
        if (mainSize && sizes.indexOf(mainSize) !== -1) {
          state.size = mainSize;
          return;
        }
        if (mainSize && sizeRank(mainSize) !== -1) {
          // Main size is not offered in the partner (hoodie 3XL, sweatpants stop
          // at 2XL): take the largest partner size that is not bigger.
          var mainRank = sizeRank(mainSize);
          var candidate = null;
          sizes.forEach(function (s) {
            var r = sizeRank(s);
            if (r !== -1 && r <= mainRank && (candidate === null || r > sizeRank(candidate))) candidate = s;
          });
          if (candidate) {
            state.size = candidate;
            if (sizeRank(candidate) < mainRank) state.sizeNote = 'Largest size in the ' + partnerWord + '.';
            return;
          }
        }
        state.size = sizes[0];
      }

      function renderPills(container, values, current, onPick) {
        container.innerHTML = '';
        values.forEach(function (value) {
          var pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'cro-set__pill';
          pill.textContent = value;
          pill.setAttribute('aria-pressed', value === current ? 'true' : 'false');
          pill.addEventListener('click', function () {
            onPick(value);
          });
          container.appendChild(pill);
        });
      }

      function renderPartnerTiles() {
        if (!partnerList) return;
        partnerList.innerHTML = '';
        partners.forEach(function (p) {
          var tile = document.createElement('button');
          tile.type = 'button';
          tile.className = 'cro-set__partner';
          tile.setAttribute('role', 'radio');
          tile.setAttribute('aria-checked', p === state.partner ? 'true' : 'false');
          var img = document.createElement('img');
          img.src = p.image;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          img.loading = 'lazy';
          var label = document.createElement('span');
          label.textContent = p.title;
          tile.appendChild(img);
          tile.appendChild(label);
          tile.addEventListener('click', function () {
            if (state.partner === p) return;
            state.partner = p;
            state.userColor = false;
            state.userSize = false;
            chooseDefaults();
            render();
          });
          partnerList.appendChild(tile);
        });
      }

      function showNote(text, isError) {
        if (!note) return;
        note.textContent = text || '';
        note.hidden = !text;
        note.classList.toggle('cro-set__note--error', !!isError);
      }

      function render() {
        var p = state.partner;
        var variant = findVariant(p, state.color, state.size);
        var colors = partnerColors(p);
        var sizes = partnerSizes(p, state.color);

        renderPartnerTiles();

        if (image) {
          var src = (variant && variant.image) || p.image;
          if (src && image.getAttribute('src') !== src) image.setAttribute('src', src);
          image.alt = p.title;
        }
        if (link) link.href = p.url;
        if (titleLink) {
          titleLink.href = p.url;
          titleLink.textContent = p.title;
        }

        if (colorsWrap && colorPills) {
          if (colors.length > 1) {
            colorsWrap.hidden = false;
            renderPills(colorPills, colors, state.color, function (value) {
              state.color = value;
              state.userColor = true;
              state.userSize = false;
              chooseDefaults();
              render();
            });
          } else {
            colorsWrap.hidden = true;
          }
        }

        if (sizePills) {
          renderPills(sizePills, sizes, state.size, function (value) {
            state.size = value;
            state.userSize = true;
            state.sizeNote = '';
            render();
          });
        }

        var setPriceText = '';
        if (offerPrice) {
          var mainPrice = state.main && typeof state.main.price === 'number' ? state.main.price : null;
          var partnerPrice = variant ? variant.price : p.variants[0].price;
          if (mainPrice !== null && partnerPrice) {
            var together = (mainPrice + partnerPrice) / 100;
            if (setLive && saving > 0) {
              setPriceText = formatMoney(together - saving);
              offerPrice.textContent = setPriceText;
              if (offerCompare) {
                offerCompare.textContent = formatMoney(together);
                offerCompare.hidden = false;
              }
              if (offerBadge) {
                offerBadge.textContent = 'Save ' + formatMoney(saving) + ' + free shipping';
                offerBadge.hidden = false;
              }
            } else {
              setPriceText = formatMoney(together);
              offerPrice.textContent = setPriceText;
              if (offerCompare) offerCompare.hidden = true;
              if (offerBadge) offerBadge.hidden = true;
            }
          }
        }

        if (button) {
          var mainOk = !state.main || state.main.available !== false;
          button.disabled = !variant || !variant.available || !mainOk;
          buttonLabel = setPriceText ? 'Add both to cart · ' + setPriceText : 'Add both to cart';
          if (!button.hasAttribute('data-busy')) button.textContent = buttonLabel;
        }
        showNote(state.sizeNote, false);
      }

      function addBoth() {
        var form = document.getElementById(formId);
        var idInput = form ? form.querySelector('input[name="id"]') : null;
        var mainId = idInput ? parseInt(idInput.value, 10) : NaN;
        var variant = findVariant(state.partner, state.color, state.size);
        if (!mainId || !variant) {
          showNote('Pick a ' + partnerWord + ' size first.', true);
          return;
        }
        var props = {};
        if (form) {
          form.querySelectorAll('input[name^="properties["]').forEach(function (input) {
            var m = input.name.match(/^properties\[(.+)\]$/);
            if (m && input.value) props[m[1]] = input.value;
          });
        }
        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var sectionIds = cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map(function (s) { return s.id; })
          : [];
        var body = {
          items: [
            { id: mainId, quantity: 1, properties: props },
            { id: variant.id, quantity: 1 },
          ],
        };
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var addUrl = (window.routes && window.routes.cart_add_url) || root + 'cart/add';
        if (addUrl.indexOf('.js') === -1) addUrl += '.js';

        button.disabled = true;
        button.setAttribute('data-busy', '1');
        button.textContent = 'Adding both...';
        showNote('', false);

        fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then(function (r) {
            return r.json().then(function (data) { return { ok: r.ok, data: data }; });
          })
          .then(function (res) {
            var data = res.data || {};
            var items = data.items || [];
            if (!res.ok || !items.length) {
              showNote(data.description || data.message || 'Something went wrong. Try adding each piece on its own.', true);
              button.textContent = buttonLabel;
              return;
            }
            if (cart && typeof cart.renderContents === 'function' && data.sections) {
              cart.renderContents({ key: items[0].key, sections: data.sections });
              var holder = document.getElementById('cart-notification-product');
              var html = data.sections['cart-notification-product'];
              if (holder && html && items[1]) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var first = doc.querySelector('[id="cart-notification-product-' + items[0].key + '"]');
                var second = doc.querySelector('[id="cart-notification-product-' + items[1].key + '"]');
                if (first && second) {
                  holder.classList.add('cro-set-double');
                  holder.innerHTML =
                    '<div class="cro-set-line">' + first.innerHTML + '</div>' +
                    '<div class="cro-set-line">' + second.innerHTML + '</div>';
                }
              }
            } else {
              window.location.href = (window.routes && window.routes.cart_url) || root + 'cart';
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: mainId, cartData: data });
            }
            button.textContent = 'Both added';
            window.setTimeout(function () {
              button.removeAttribute('data-busy');
              button.textContent = buttonLabel;
            }, 2500);
          })
          .catch(function () {
            showNote('Something went wrong. Try adding each piece on its own.', true);
            button.textContent = buttonLabel;
          })
          .then(function () {
            button.disabled = false;
            if (button.textContent !== 'Both added') button.removeAttribute('data-busy');
          });
      }

      if (button) button.addEventListener('click', addBoth);

      onVariantChange(function (event) {
        var data = event && event.data ? event.data : {};
        if (data.sectionId && String(data.sectionId) !== String(sectionId)) return;
        if (data.variant) state.main = data.variant;
        else state.main = readSelectedVariant(sectionId);
        chooseDefaults();
        render();
      });

      chooseDefaults();
      render();
    });
  }

  /* ------------------------------------------------------------------ boot */
  ready(function () {
    syncLabelStrings();
    renderDelivery();
    watchCartFooter();
    initSizeGuide();
    initFitFinderLinks();
    initStickyBar();
    initCartSticky();
    initSamePrinciple();
    initCompleteTheSet();
    // Let Dawn's custom elements finish upgrading before dispatching a change.
    window.setTimeout(selectDefaultColour, 0);
  });
})();
